import { useEffect, useState, useMemo, useRef } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ScrollElement from '../../../global/Elements/ScrollElement';
import { searchContext, userAppContext } from '../../../context/Context';
import { scroller } from 'react-scroll';
import { ScoreBreakdownPanel } from '@/components/ScoreBreakdown/ScoreBreakdownPanel';
import { useScoreBreakdownStore } from '@/store/scoreBreakdownStore';
import { usePrevalenceBreakdownStore } from '@/store/prevalenceBreakdownStore';
import { PrevalenceBreakdownPanel } from '@/components/PrevalenceBreakdown/PrevalenceBreakdownPanel';
import EvidenceTimelinePanel from '@/components/EvidenceTimeline/EvidenceTimelinePanel';
import { useEvidenceTimelineStore } from '@/store/evidenceTimelineStore';

import ManageGrid from './components/table/ManageGrid';
import ManageGraph from './components/table/ManageGraph';

import SummaryGDAGraphs from '@/components/Graphs/SummaryGDA/SummaryGDAGraphs';
import SummaryVDAGraphs from '@/components/Graphs/SummaryVDA/SummaryVDAGraphs';

import { useNavigate, useSearchParams } from 'react-router-dom';
import MappingResults from '@/components/Mapping/MappingResults';
import { MappingDrillDownProvider } from '@/context/MappingDrillDownContext';
import DrillDownTableContainer from '@/components/Mapping/DrillDownTableContainer';
import { SearchContext, UserContext } from '../../../types';
import SharedButton from '../../../global/Button/Button';
import { getTabList } from './helpers/tableHelpers';
import DDAGraphs from '@/components/Graphs/DDA/DDAGraphs';
import { trackBrowseTabClick } from '../../../utils/analytics';
import ScoreModal from './components/modal/ScoreModal';
import { AdjustmentsHorizontalIcon, ChevronDoubleLeftIcon } from '@heroicons/react/16/solid';
import { deleteFilter } from '../../../services/savedFiltersService';
import updateParams from '../../../utils/updateParams';
import { mapItemsToSearchItems } from '../../../components/Table/types/index';
import { useRightSidebarLayout } from './context/RightSidebarLayoutContext';
import SideBarContent from '@/components/QueriedContent/SideBarContent';
import GeneInfoPanelContent from '@/components/GeneContent/GeneInfoPanelContent';
import { RightAsideSidebar } from './components/table/components/RightAsideSidebar';
import { NAVBAR_HEIGHT_PX } from '@/router/AppRouter';
import { MappingSidebarColumnProvider, useMappingSidebarColumn } from '@/context/MappingSidebarColumnContext';
import { useMappingStackStore, useCurrentMappingLevel } from '@/store/mappingStackStore';
import useScrollWindow from '@/hooks/useScrollWindow';
import { useAlertSystem } from './components/table/hooks';
import { useTableStore } from '@/store/tableStore';
import {
  MAPPING_URL_PARAM,
  decodeMappingStateFromUrl,
  encodeMappingStateForUrl,
} from '@/utils/mappingUrlState';
import SharedBadge from '@/global/Badge/Badge';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import { getTitle, getName, DATASETS } from '@/components/Constants';

/** Misma lógica de etiqueta que en Table controls → Search items; tooltip muestra el id. */
function getSearchItemBadgeLabel(item: any): { label: string; tooltipId: string } {
  const title = getTitle(item);
  const id = getName(item);
  const titleStr =
    typeof title === 'object' && title !== null && 'title' in title
      ? String((title as { title?: string }).title ?? id)
      : String(title ?? '');
  const idStr = String(id ?? item?.id ?? '');
  const isAllDataset =
    typeof title === 'object' &&
    title !== null &&
    'title' in (title as object) &&
    (title as { title?: string }).title === DATASETS.ALL;
  const label = titleStr && !isAllDataset ? titleStr : idStr;
  return { label, tooltipId: idStr };
}

interface SearchResultsProps {
  state: any;
  dispatch: any;
}

function SearchResultsInner({ state, dispatch }: SearchResultsProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { view, source, tab, searchItems, setSearchItems, geneContent, setGeneContent, mappingContent } =
    searchContext() as unknown as SearchContext;
  const { isAuthenticated, groups, restrictChemical } = userAppContext() as unknown as UserContext;
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);
  const { showSuccessAlert } = useAlertSystem();
  const savedColumsApplied = useTableStore(s => s.savedColumsApplied);
  const setSavedColumsApplied = useTableStore(s => s.setSavedColumsApplied);

  const scoreBreakdownRow = useScoreBreakdownStore(s => s.scoreBreakdownRow);
  const setScoreBreakdownRow = useScoreBreakdownStore(s => s.setScoreBreakdownRow);
  const prevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.prevalenceBreakdownRow);
  const setPrevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.setPrevalenceBreakdownRow);
  const evidenceTimelineRow = useEvidenceTimelineStore(s => s.evidenceTimelineRow);
  const setEvidenceTimelineRow = useEvidenceTimelineStore(s => s.setEvidenceTimelineRow);

  useEffect(() => {
    if (savedColumsApplied) {
      showSuccessAlert(`Column configuration "${savedColumsApplied.name}" applied successfully`);
      setSavedColumsApplied(null);
    }
  }, [savedColumsApplied]);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedView, setSelectedView] = useState<'tables' | 'graphs'>(() => {
    const display = searchParams.get('display');
    return display === 'graphs' ? 'graphs' : 'tables';
  });
  const displayParam = searchParams.get('display');
  useEffect(() => {
    setSelectedView(displayParam === 'graphs' ? 'graphs' : 'tables');
  }, [displayParam]);
  const [defaultFilterValue, _setDefaultFilterValue] = useState([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [refreshScoresDropdown, setRefreshScoresDropdown] = useState(0);
  const [leftInfoPanelItem, setLeftInfoPanelItem] = useState<any>(null);
  const [sourceWeights, setSourceWeights] = useState<Array<{ name: string; score: number }>>(() => {
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return [];
      }
    }
    return [];
  });

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const { isSidebarOpen, sidebarWidth, setSidebarOpen, expandSearchItemsSection } = useRightSidebarLayout();
  const [graphsColumnModel, setGraphsColumnModel] = useState<Record<string, boolean>>({});

  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;

  const [summaryColumnModel, setSummaryColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [summaryColumnOrder, setSummaryColumnOrder] = useState<string[] | undefined>(undefined);
  const [evidenceColumnModel, setEvidenceColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [evidenceColumnOrder, setEvidenceColumnOrder] = useState<string[] | undefined>(undefined);
  const [activeColumnPanel, setActiveColumnPanel] = useState<'summary' | 'evidence'>('summary');

  const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';
  const mappingStackCurrentIndex = useMappingStackStore(s => s.currentIndex);
  const mappingStackLength = useMappingStackStore(s => s.stack.length);
  const mappingStack = useMappingStackStore(s => s.stack);
  const mappingTableStateByLevelId = useMappingStackStore(s => s.tableStateByLevelId);
  const clearMappingStack = useMappingStackStore(s => s.clearFromLevel);
  const currentMappingLevel = useCurrentMappingLevel();

  const mappingUrlRestoreDoneRef = useRef(false);
  const [isMappingUrlHydrated, setIsMappingUrlHydrated] = useState(false);

  useEffect(() => {
    if (mappingUrlRestoreDoneRef.current) return;
    mappingUrlRestoreDoneRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(MAPPING_URL_PARAM);
    if (!encoded) {
      setIsMappingUrlHydrated(true);
      return;
    }

    const decoded = decodeMappingStateFromUrl(encoded);
    if (!decoded || decoded.stack.length === 0) {
      setIsMappingUrlHydrated(true);
      return;
    }

    useMappingStackStore.setState({
      stack: decoded.stack,
      currentIndex: decoded.currentIndex,
      tableStateByLevelId: decoded.tableStateByLevelId ?? {},
    });

    setTimeout(() => setIsMappingUrlHydrated(true), 0);
  }, []);

  useEffect(() => {
    if (!isMappingUrlHydrated) return;

    const params = new URLSearchParams(window.location.search);
    const current = params.get(MAPPING_URL_PARAM);

    const hasDrillDown = mappingStack.length > 1 && mappingStackCurrentIndex > 0;
    if (!hasDrillDown) return;

    const encoded = encodeMappingStateForUrl({
      stack: mappingStack,
      currentIndex: mappingStackCurrentIndex,
      tableStateByLevelId: mappingTableStateByLevelId,
    });
    if (current === encoded) return;

    params.set(MAPPING_URL_PARAM, encoded);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`,
    );
  }, [mappingStack, mappingStackCurrentIndex, mappingTableStateByLevelId, isMappingUrlHydrated]);

  useEffect(() => {
    return () => {
      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
    };
  }, []);

  const mappingSidebar = useMappingSidebarColumn();
  const isInMappingView = useDrillDownMapping && mappingStackLength > 0 && mappingStackCurrentIndex > 0;
  const useMappingSidebarState = isInMappingView && mappingSidebar.state != null;

  const columnOrder = useMappingSidebarState
    ? mappingSidebar.state!.columnOrder
    : activeColumnPanel === 'summary'
      ? summaryColumnOrder
      : evidenceColumnOrder;

  const summaryResetRef = useRef<(() => void) | null>(null);
  const evidenceResetRef = useRef<(() => void) | null>(null);

  const handleResetToDefault = () => {
    if (useMappingSidebarState && mappingSidebar.state) {
      mappingSidebar.state.setColumnVisibilityModel(() => ({}));
      mappingSidebar.state.setColumnOrder([]);
    } else if (activeColumnPanel === 'summary') {
      summaryResetRef.current?.();
    } else {
      evidenceResetRef.current?.();
    }
  };

  const handleApplyWeights = (weights: Array<{ name: string; score: number }>) => {
    setSourceWeights(weights);
    if (weights.length > 0) {
      sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(weights));
    } else {
      sessionStorage.removeItem(`sourceWeights_${tab}`);
    }
  };

  const handleApplySavedScore = (savedScore: any) => {
    if (savedScore.sourceWeights && savedScore.sourceWeights.length > 0) {
      handleApplyWeights(savedScore.sourceWeights);

      const urlParams = new URLSearchParams(window.location.search);
      if (savedScore.sourceWeights.length > 1) {
        urlParams.set('source', 'ALL');
      } else if (savedScore.sourceWeights.length === 1) {
        urlParams.set('source', savedScore.sourceWeights[0].name);
      }

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, '', newUrl);

      window.location.reload();
    }
  };

  const handleDeleteSavedScore = async (scoreId: string) => {
    try {
      if (!scoreId.startsWith('savedScore_')) {
        await deleteFilter(scoreId);
      }
    } catch (error) {
      console.error('Error deleting saved score:', error);
    }
  };

  const handleClearWeights = () => {
    // Clear weights
    setSourceWeights([]);
    sessionStorage.removeItem(`sourceWeights_${tab}`);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source') === 'ALL') {
      // Could set to a default source, but for now just reload
    }

    window.location.reload();
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      if (activeColumnPanel === 'summary') {
        setSummaryColumnModel(savedColumn.columnVisibilityModel);
      } else {
        setEvidenceColumnModel(savedColumn.columnVisibilityModel);
      }
    }
    if (savedColumn.columnOrder) {
      if (activeColumnPanel === 'summary') {
        setSummaryColumnOrder(savedColumn.columnOrder);
      } else {
        setEvidenceColumnOrder(savedColumn.columnOrder);
      }
    }
    setSavedColumsApplied({
      name: savedColumn.name,
      columnVisibilityModel: savedColumn.columnVisibilityModel,
      columnOrder: savedColumn.columnOrder,
    });
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) {
        await deleteFilter(columnId);
      }
    } catch (error) {
      console.error('Error deleting column configuration:', error);
    }
  };

  const tabList = useMemo(() => {
    return getTabList(view as string, tab as string);
  }, [view, tab]);

  const handleRemoveSearchItem = (index: number) => {
    const remaining = (searchItems ?? []).filter((_, i) => i !== index);
    if (remaining.length === 0) {
      clearMappingStack(0);
      setSearchItems([]);
      navigate('/');
    } else {
      const params = new URLSearchParams(window.location.search);
      const newParams = updateParams({
        searchItems: remaining as any,
        view: params.get('view') ?? undefined,
        source: params.get('source') ?? undefined,
        tab: params.get('tab') ?? undefined,
      });
      navigate({ pathname: window.location.pathname, search: newParams });
    }
  };

  const handleTabSelect = (index: number) => {
    // Simply proceed with tab change - weights are now tab-specific
    proceedWithTabChange(index);
  };

  const proceedWithTabChange = (index: number) => {
    setTabIndex(index);
    const newTab = tabList[index].key;
    const tabLabel = tabList[index].label;

    trackBrowseTabClick(tabLabel);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', newTab);

    navigate({ pathname: window.location.pathname, search: `?${currentParams.toString()}` });
  };

  useEffect(() => {
    // Update nav component state based on app state
    const newIndex = tabList.findIndex(t => t.key === tab);
    setTabIndex(newIndex);

    // Load weights for the new tab
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        setSourceWeights(JSON.parse(stored));
      } catch (e) {
        setSourceWeights([]);
      }
    } else {
      setSourceWeights([]);
    }
  }, [tab, tabList]);

  // Reset controlled column models when the tab actually changes
  const prevTabForResetRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevTabForResetRef.current === null || prevTabForResetRef.current === tab) {
      prevTabForResetRef.current = tab as string;
      return;
    }
    prevTabForResetRef.current = tab as string;
    setSummaryColumnModel(undefined);
    setEvidenceColumnModel(undefined);
    setSummaryColumnOrder(undefined);
    setEvidenceColumnOrder(undefined);
  }, [tab]);

  // Table/Graph button
  // Button is only shown on Association views, not on entity list tabs (DISEASES, GENES, VARIANTS, CHEMICALS)
  const entityListTabs = ['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'];
  const showManageGraph = view !== 'ALL' && view !== 'CHEMICALS' && !entityListTabs.includes(tab as string);
  const visibleTabs = selectedView === 'graphs' ? tabList.slice(0, -1) : tabList;

  // Grid view items
  const urlParams = new URLSearchParams(window.location.search);
  const isEvidenceParam = urlParams.get('isEvidence');

  let showSummary = true;
  let showEvidence = true;

  if (isEvidenceParam === 'false') {
    showEvidence = false;
  } else if (isEvidenceParam === 'true') {
    showSummary = false;
  } else {
    if (tab === 'DDA') showEvidence = false;
    // Entity list tabs (DISEASES, GENES, VARIANTS, CHEMICALS) only show summary table
    if (['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'].includes(tab as string)) showEvidence = false;
    if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) showEvidence = false;
  }

  if (entityListTabs.includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }
  if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }

  useEffect(() => {
    if (!showEvidence) setActiveColumnPanel('summary');
  }, [showEvidence]);

  // Graph view items
  const graphMap = {
    GDA: SummaryGDAGraphs,
    VDA: SummaryVDAGraphs,
    DDA: DDAGraphs,
  };

  useEffect(() => {
    if (scoreBreakdownRow) {
      setLeftInfoPanelItem(null);
      setPrevalenceBreakdownRow(null);
    }
  }, [scoreBreakdownRow]);

  useEffect(() => {
    if (prevalenceBreakdownRow) {
      setLeftInfoPanelItem(null);
      setScoreBreakdownRow(null);
    }
  }, [prevalenceBreakdownRow]);

  const handleOpenEntityInfo = (item: any) => {
    setScoreBreakdownRow(null);
    setPrevalenceBreakdownRow(null);
    setLeftInfoPanelItem(item);
  };

  const prevSearchItemsLengthRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      prevSearchItemsLengthRef.current !== null &&
      prevSearchItemsLengthRef.current > 0 &&
      searchItems?.length === 0
    ) {
      setSelectedView('tables');
      setSearchParams(
        prev => {
          const u = new URLSearchParams(prev);
          u.delete('display');
          return u;
        },
        { replace: true },
      );
    }
    prevSearchItemsLengthRef.current = searchItems?.length ?? 0;
  }, [searchItems]);

  // scroll to ref animation
  const scrollToRef = (ref: string) => {
    scroller.scrollTo(ref, {
      duration: 750,
      // delay: 50,
      // smooth: 'easeInOutQuad',
      smooth: true,
    });
  };

  useEffect(() => {
    if (geneContent?.isActive && geneContent.geneInfo?.id) {
      handleOpenEntityInfo({
        id: geneContent.geneInfo.id,
        symbol: geneContent.geneInfo.symbol,
        entityView: 'GENES',
      });
      setGeneContent({ isActive: false, geneInfo: null });
    }
  }, [geneContent?.isActive]);

  useEffect(() => {
    if (mappingContent!.isActive) {
      scrollToRef('Mapping');
    }
  }, [mappingContent!.isActive]);

  // Auto-scroll to Evidence table if URL has only Evidence filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let hasEvidenceFilters = false;
    let hasSummaryFilters = false;

    for (const [key] of params.entries()) {
      if (key.startsWith('filters-evidence-') || key.startsWith('filters[evidence][')) {
        hasEvidenceFilters = true;
      }
      if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) {
        hasSummaryFilters = true;
      }
    }

    if (hasEvidenceFilters && !hasSummaryFilters && showEvidence) {
      // Small delay to ensure the component is mounted
      setTimeout(() => {
        scrollToRef('Evidence');
      }, 300);
    }
  }, [showEvidence]);

  return (
    <>
      {/* Score Modal */}
      <ScoreModal
        isOpen={scoreModalOpen}
        onClose={() => setScoreModalOpen(false)}
        onApplyWeights={handleApplyWeights}
        onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
        refreshTrigger={refreshScoresDropdown}
      />

      {isTrial && (
        <div className="info-container p-2 text-center">
          <p style={{ fontSize: '18px', color: 'rgb(190,0,128)', paddingTop: '10px' }}>
            Only the top 30 results are shown.{' '}
            <a href="/academic-plans" style={{ color: '#0d1661' }}>
              Upgrade your account{' '}
            </a>{' '}
            to see all results.{' '}
            <a href="/plans" style={{ color: '#0d1661' }}>
              View our plans
            </a>
            .
          </p>
        </div>
      )}
      {leftInfoPanelItem && (
        <aside
          role="complementary"
          aria-label="Item details"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          {leftInfoPanelItem?.entityView === 'GENES' ? (
            <GeneInfoPanelContent
              geneId={String(leftInfoPanelItem.id ?? leftInfoPanelItem.geneId ?? '')}
              geneSymbol={String(leftInfoPanelItem.symbol ?? leftInfoPanelItem.geneSymbol ?? '')}
              onClose={() => setLeftInfoPanelItem(null)}
            />
          ) : (
            <SideBarContent
              selectedInfoItem={leftInfoPanelItem}
              view={view}
              showModalExample={false}
              setShowModalExample={() => {}}
              renderInPanel
              onPanelClose={() => setLeftInfoPanelItem(null)}
            />
          )}
        </aside>
      )}

      {scoreBreakdownRow && !leftInfoPanelItem && (
        <aside
          role="complementary"
          aria-label="Score breakdown"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <ScoreBreakdownPanel rowData={scoreBreakdownRow} onClose={() => setScoreBreakdownRow(null)} />
        </aside>
      )}
      {prevalenceBreakdownRow && !leftInfoPanelItem && !scoreBreakdownRow && (
        <aside
          role="complementary"
          aria-label="Prevalence breakdown"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <PrevalenceBreakdownPanel
            rowData={prevalenceBreakdownRow}
            onClose={() => setPrevalenceBreakdownRow(null)}
          />
        </aside>
      )}
      {evidenceTimelineRow && !leftInfoPanelItem && !scoreBreakdownRow && !prevalenceBreakdownRow && (
        <aside
          role="complementary"
          aria-label="Evidence timeline"
          className="fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden"
          style={{
            top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
            height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
            width: isDesktop ? 340 : '100vw',
            minWidth: isDesktop ? 340 : '100vw',
          }}
        >
          <EvidenceTimelinePanel rowData={evidenceTimelineRow} onClose={() => setEvidenceTimelineRow(null)} />
        </aside>
      )}
      {selectedView === 'graphs' && (
        <RightAsideSidebar
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          source={source}
          sourceWeights={sourceWeights}
          view={view}
          tab={tab}
          isEvidence={false}
          columnVisibilityModel={graphsColumnModel}
          setColumnVisibilityModel={updater => setGraphsColumnModel(updater)}
          searchItems={mapItemsToSearchItems(searchItems)}
          onOpenSearchItemInfo={handleOpenEntityInfo}
          onRemoveSearchItem={handleRemoveSearchItem}
          onClearAllSearchItems={() => {
            clearMappingStack(0);
            setSearchItems([]);
            navigate('/');
          }}
          isMapping={false}
          showMyScoreSection={!isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated}
          onOpenScoreModal={() => setScoreModalOpen(true)}
          onApplySavedColumns={handleApplySavedColumns}
          onDeleteSavedColumns={handleDeleteSavedColumn}
          onApplySavedScore={handleApplySavedScore}
          onDeleteSavedScore={handleDeleteSavedScore}
          onClearWeights={handleClearWeights}
          hasActiveWeights={sourceWeights.length > 0}
          refreshScoresDropdown={refreshScoresDropdown}
        />
      )}
      {selectedView === 'tables' && (
        <RightAsideSidebar
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          source={source}
          sourceWeights={sourceWeights}
          view={view}
          tab={tab}
          isEvidence={
            useMappingSidebarState
              ? (mappingSidebar.state!.columnPanelToggle?.active ?? 'summary') === 'evidence'
              : showEvidence && activeColumnPanel === 'evidence'
          }
          columnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.columnVisibilityModel
              : activeColumnPanel === 'summary' || !showEvidence
                ? (summaryColumnModel ?? {})
                : (evidenceColumnModel ?? {})
          }
          setColumnVisibilityModel={
            useMappingSidebarState
              ? mappingSidebar.state!.setColumnVisibilityModel
              : updater => {
                  if (activeColumnPanel === 'summary' || !showEvidence) {
                    setSummaryColumnModel(prev =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  } else {
                    setEvidenceColumnModel(prev =>
                      typeof updater === 'function' ? updater(prev ?? {}) : updater,
                    );
                  }
                }
          }
          columnPanelToggle={
            useMappingSidebarState
              ? mappingSidebar.state!.columnPanelToggle
              : showEvidence
                ? { active: activeColumnPanel, onChange: setActiveColumnPanel }
                : undefined
          }
          searchItems={mapItemsToSearchItems(searchItems)}
          onOpenSearchItemInfo={handleOpenEntityInfo}
          onRemoveSearchItem={handleRemoveSearchItem}
          onClearAllSearchItems={() => {
            clearMappingStack(0);
            setSearchItems([]);
            navigate('/');
          }}
          isMapping={useMappingSidebarState}
          showMyScoreSection={!isAcademic && ['GDA', 'VDA'].includes(tab as string) && isAuthenticated}
          onOpenScoreModal={() => setScoreModalOpen(true)}
          onApplySavedColumns={handleApplySavedColumns}
          onDeleteSavedColumns={handleDeleteSavedColumn}
          onApplySavedScore={handleApplySavedScore}
          onDeleteSavedScore={handleDeleteSavedScore}
          onClearWeights={handleClearWeights}
          hasActiveWeights={sourceWeights.length > 0}
          refreshScoresDropdown={refreshScoresDropdown}
          mappingContent={
            useMappingSidebarState && currentMappingLevel?.mappingContent
              ? (currentMappingLevel.mappingContent as any)
              : mappingContent
          }
          columnOrder={columnOrder}
          isAcademic={isAcademic}
          onResetToDefault={handleResetToDefault}
        />
      )}
      <div
        className={`search-results-container ${selectedView === 'tables' ? 'flex' : ''}`}
        data-testid="search-results"
        style={{
          marginLeft:
            leftInfoPanelItem || scoreBreakdownRow || prevalenceBreakdownRow || evidenceTimelineRow
              ? isDesktop
                ? 340
                : 0
              : 0,
          marginRight: isSidebarOpen ? (isDesktop ? sidebarWidth : 0) : 0,
          transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out',
          paddingRight: isSidebarOpen ? '1%' : '3%',
          paddingLeft:
            leftInfoPanelItem || scoreBreakdownRow || prevalenceBreakdownRow || evidenceTimelineRow
              ? '1%'
              : '3%',
        }}
      >
        <div
          className="pr-1 pl-1 md:p-5 md:pr-5 responsive-search-results"
          style={{ flex: '1', height: '100%', minWidth: 0 }}
        >
          <Tabs
            className="react-tabs superposed-content"
            selectedIndex={tabIndex}
            onSelect={index => handleTabSelect(index)}
          >
            <div className="d-flex gap-2 w-full lg:justify-end">
              {!isSidebarOpen && (
                <BaseTooltip content="Open table controls" position="left">
                  <SharedButton
                    type="button"
                    variant="terciary"
                    onClick={() => setSidebarOpen(!isSidebarOpen)}
                    arrow={isDesktop ? null : isSidebarOpen ? 'down' : 'up'}
                    aria-expanded={isSidebarOpen}
                    aria-label={isSidebarOpen ? 'Hide table controls' : 'Open table controls'}
                    data-testid="grid-controls-sidebar-toggle"
                    size="xs"
                    iconLeft={<ChevronDoubleLeftIcon className="w-5" />}
                    iconMargin={null}
                    className="p-0 mb-4"
                  >
                    {!isDesktop && (
                      <>
                        <AdjustmentsHorizontalIcon className="w-5 mr-2" />
                        {isSidebarOpen ? 'Hide controls' : 'Controls'}
                      </>
                    )}
                  </SharedButton>
                </BaseTooltip>
              )}
            </div>
            <div className="responsive-search-tabs min-w-0 lg:flex lg:justify-between lg:items-center lg:gap-2">
              <div className="d-flex w-full min-w-0 gap-2 md:w-3/4 md:justify-start">
                <TabList className="react-tabs__tab-list col-auto">
                  {visibleTabs.map(t => (
                    <Tab key={t.key} className={'react-tabs__tab bg-white mb-0 my-2 my-lg-0'}>
                      {t.label}
                    </Tab>
                  ))}
                </TabList>
              </div>
              {!isSidebarOpen && (
                <div className="flex min-w-0 flex-col items-end lg:max-w-[50%]">
                  <div className="flex w-full min-w-0 flex-nowrap items-center justify-end gap-2 overflow-x-auto">
                    {(searchItems.length > 4 ? searchItems.slice(0, 4) : searchItems).map(item => {
                      const { label, tooltipId } = getSearchItemBadgeLabel(item);
                      return (
                        <div key={item.id} className="w-[11rem] max-w-[11rem] shrink-0 overflow-hidden">
                          <BaseTooltip
                            content={`${tooltipId} - ${label}`}
                            position="bottom"
                            className="block w-full min-w-0"
                            contentClassName="max-w-xs whitespace-normal break-words text-left"
                          >
                            <SharedBadge
                              text={<span className="min-w-0 flex-1 truncate">{label}</span>}
                              variant="secondary"
                              className="!mb-0 flex h-7 w-full min-w-0 max-w-full justify-start overflow-hidden text-left"
                            />
                          </BaseTooltip>
                        </div>
                      );
                    })}
                    {searchItems.length > 4 && (
                      <SharedBadge
                        text={`+ ${searchItems.length - 4}`}
                        variant="secondary"
                        className="shrink-0 text-center justify-center h-7"
                        onClick={() => {
                          setSidebarOpen(true);
                          expandSearchItemsSection();
                        }}
                        aria-label={`Ver ${searchItems.length - 4} elementos más en controles de tabla`}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Score Configuration Info Message */}
            {sourceWeights.length > 0 &&
              selectedView === 'tables' &&
              ['GDA', 'VDA'].includes(tab as string) &&
              !isAcademic &&
              isAuthenticated && (
                <div className="mb-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900">Custom Score Configuration Active</p>
                      <p className="text-xs text-blue-700 mt-1 mb-0">
                        You have a custom My Score configuration applied to this data.
                        {isAuthenticated ? (
                          <span>
                            {' '}
                            Use the "My Score" section in the Controls sidebar to manage or clear your
                            configuration.
                          </span>
                        ) : (
                          <span>
                            {' '}
                            Use the "My Score" section in the Controls sidebar to modify or clear your
                            configuration.
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

            <div className="mt-10">
              {tabList.map(t => (
                <TabPanel key={t.key} className="tab-pane">
                  <>
                    {useDrillDownMapping && selectedView === 'tables' ? (
                      <MappingDrillDownProvider useDrillDown={true}>
                        <DrillDownTableContainer
                          baseLevelId={`base-${t.key}`}
                          baseLabel="Results"
                          dispatch={dispatch}
                          panelTab={t.key}
                          onOpenEntityInfo={handleOpenEntityInfo}
                          isMappingUrlHydrated={isMappingUrlHydrated}
                        >
                          <ScrollElement name="Summary">
                            {showSummary && (
                              <ManageGrid
                                source={source}
                                view={view}
                                tab={tab}
                                panelTab={t.key}
                                searchItems={mapItemsToSearchItems(searchItems)}
                                setSearchItems={setSearchItems}
                                isEvidence={false}
                                isMapping={false}
                                restrictChemical={restrictChemical}
                                isInfoSidebarOpen={isInfoSidebarOpen}
                                setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                                defaultFilterValue={defaultFilterValue}
                                dispatch={dispatch}
                                sourceWeights={sourceWeights}
                                onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                                onOpenSearchItemInfo={handleOpenEntityInfo}
                                columnVisibilityModel={summaryColumnModel}
                                setColumnVisibilityModel={updaterOrValue =>
                                  setSummaryColumnModel(prev =>
                                    typeof updaterOrValue === 'function'
                                      ? updaterOrValue(prev ?? {})
                                      : updaterOrValue,
                                  )
                                }
                                columnOrder={summaryColumnOrder}
                                setColumnOrder={order => setSummaryColumnOrder(order)}
                                onColumnStateInit={(isEv, model, order) => {
                                  if (!isEv) {
                                    setSummaryColumnModel(model);
                                    setSummaryColumnOrder(order ?? undefined);
                                  }
                                }}
                                showRightSidebar={false}
                                resetRef={summaryResetRef}
                              />
                            )}
                          </ScrollElement>
                          <ScrollElement name="Evidence">
                            {showEvidence && (
                              <ManageGrid
                                view={view}
                                source={source}
                                tab={tab}
                                panelTab={t.key}
                                searchItems={mapItemsToSearchItems(searchItems)}
                                setSearchItems={setSearchItems}
                                isEvidence={true}
                                isMapping={false}
                                restrictChemical={restrictChemical}
                                setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                                isInfoSidebarOpen={isInfoSidebarOpen}
                                defaultFilterValue={defaultFilterValue}
                                dispatch={dispatch}
                                sourceWeights={sourceWeights}
                                onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                                onOpenSearchItemInfo={handleOpenEntityInfo}
                                columnVisibilityModel={evidenceColumnModel}
                                setColumnVisibilityModel={updaterOrValue =>
                                  setEvidenceColumnModel(prev =>
                                    typeof updaterOrValue === 'function'
                                      ? updaterOrValue(prev ?? {})
                                      : updaterOrValue,
                                  )
                                }
                                columnOrder={evidenceColumnOrder}
                                setColumnOrder={order => setEvidenceColumnOrder(order)}
                                onColumnStateInit={(isEv, model, order) => {
                                  if (isEv) {
                                    setEvidenceColumnModel(model);
                                    setEvidenceColumnOrder(order ?? undefined);
                                  }
                                }}
                                showRightSidebar={false}
                                resetRef={evidenceResetRef}
                              />
                            )}
                          </ScrollElement>
                        </DrillDownTableContainer>
                      </MappingDrillDownProvider>
                    ) : (
                      <>
                        <ScrollElement name="Summary">
                          {selectedView === 'tables' && showSummary && (
                            <ManageGrid
                              source={source}
                              view={view}
                              tab={tab}
                              panelTab={t.key}
                              searchItems={mapItemsToSearchItems(searchItems)}
                              setSearchItems={setSearchItems}
                              isEvidence={false}
                              isMapping={false}
                              restrictChemical={restrictChemical}
                              isInfoSidebarOpen={isInfoSidebarOpen}
                              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                              defaultFilterValue={defaultFilterValue}
                              dispatch={dispatch}
                              sourceWeights={sourceWeights}
                              onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                              onOpenSearchItemInfo={handleOpenEntityInfo}
                              columnVisibilityModel={summaryColumnModel}
                              setColumnVisibilityModel={updaterOrValue =>
                                setSummaryColumnModel(prev =>
                                  typeof updaterOrValue === 'function'
                                    ? updaterOrValue(prev ?? {})
                                    : updaterOrValue,
                                )
                              }
                              columnOrder={summaryColumnOrder}
                              setColumnOrder={order => setSummaryColumnOrder(order)}
                              onColumnStateInit={(isEv, model, order) => {
                                if (!isEv) {
                                  setSummaryColumnModel(model);
                                  setSummaryColumnOrder(order ?? undefined);
                                }
                              }}
                              showRightSidebar={false}
                            />
                          )}
                          {selectedView === 'graphs' && showManageGraph && (
                            <ManageGraph
                              source={source}
                              view={view}
                              tab={tab}
                              searchItems={searchItems}
                              setSearchItems={setSearchItems}
                              sectionTitle={t.label}
                              names={searchItems}
                              title={view}
                              Graph={graphMap[t.key]}
                            />
                          )}
                        </ScrollElement>
                        <ScrollElement name="Evidence">
                          {selectedView === 'tables' && showEvidence && (
                            <ManageGrid
                              view={view}
                              source={source}
                              tab={tab}
                              panelTab={t.key}
                              searchItems={mapItemsToSearchItems(searchItems)}
                              setSearchItems={setSearchItems}
                              isEvidence={true}
                              isMapping={false}
                              restrictChemical={restrictChemical}
                              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
                              isInfoSidebarOpen={isInfoSidebarOpen}
                              defaultFilterValue={defaultFilterValue}
                              dispatch={dispatch}
                              sourceWeights={sourceWeights}
                              onScoreSaved={() => setRefreshScoresDropdown(prev => prev + 1)}
                              onOpenSearchItemInfo={handleOpenEntityInfo}
                              columnVisibilityModel={evidenceColumnModel}
                              setColumnVisibilityModel={updaterOrValue =>
                                setEvidenceColumnModel(prev =>
                                  typeof updaterOrValue === 'function'
                                    ? updaterOrValue(prev ?? {})
                                    : updaterOrValue,
                                )
                              }
                              columnOrder={evidenceColumnOrder}
                              setColumnOrder={order => setEvidenceColumnOrder(order)}
                              onColumnStateInit={(isEv, model, order) => {
                                if (isEv) {
                                  setEvidenceColumnModel(model);
                                  setEvidenceColumnOrder(order ?? undefined);
                                }
                              }}
                              showRightSidebar={false}
                            />
                          )}
                        </ScrollElement>{' '}
                        <ScrollElement name="Mapping">
                          {selectedView === 'tables' && mappingContent?.isActive && (
                            <MappingResults mappingContent={mappingContent} dispatch={dispatch} />
                          )}
                        </ScrollElement>
                      </>
                    )}
                  </>
                </TabPanel>
              ))}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
}

function SearchResults(props: SearchResultsProps) {
  return (
    <MappingSidebarColumnProvider>
      <SearchResultsInner {...props} />
    </MappingSidebarColumnProvider>
  );
}

export default SearchResults;
